import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { TenantProfileDocumentStatus } from '@libs/constants';
import { useTrans } from '@libs/i18n';

import { CategoryPanel } from './components/CategoryPanel';
import { CategoryTabs } from './components/CategoryTabs';
import { GlobalUploadBar } from './components/GlobalUploadBar';
import { InFlightDocumentsBar } from './components/InFlightDocumentsBar';
import { ProfileHeader } from './components/ProfileHeader';
import { ProfileHero } from './components/ProfileHero';
import { ProfileSummaryRow } from './components/ProfileSummaryRow';
import { ORG_TOKENS } from './utils/tokens';
import { useOrganizationProfileData } from './utils/useOrganizationProfileData';

/**
 * Organization Profile page. Unifies the "why we ask" intro, summary cards,
 * global document upload, category tabs (with derived statuses), and the
 * per-category read-only/edit field panel. Reuses the existing tenant-profile
 * API hooks; no backend changes.
 */
export const OrganizationProfilePage: FC = observer(() => {
  const { t } = useTrans();
  const navigate = useNavigate();

  const {
    outline,
    docs,
    summary,
    derivedById,
    completedCount,
    categoryCount,
    isLoading,
    isError,
  } = useOrganizationProfileData();

  const [activeId, setActiveId] = useState<string | null>(null);

  // Default the active tab to the first category once the outline loads, and
  // recover if the active category disappears (e.g. template re-seeded).
  useEffect(() => {
    if (!outline?.categories.length) return;
    const stillValid =
      activeId && outline.categories.some((c) => c.id === activeId);
    if (!stillValid) setActiveId(outline.categories[0].id);
  }, [outline, activeId]);

  // Document tallies for the "documents uploaded" summary card subline.
  const docCounts = useMemo(() => {
    let approved = 0;
    let processing = 0;
    for (const doc of docs) {
      if (doc.status === TenantProfileDocumentStatus.DONE) approved += 1;
      else if (
        doc.status === TenantProfileDocumentStatus.CLASSIFYING ||
        doc.status === TenantProfileDocumentStatus.EXTRACTING
      ) {
        processing += 1;
      }
    }
    return { approved, processing };
  }, [docs]);

  const activeMeta = useMemo(
    () => outline?.categories.find((c) => c.id === activeId) ?? null,
    [outline, activeId],
  );

  const goToHub = () => navigate('/tenant-profile/document-hub');
  // Open the hub pre-filtered to documents awaiting review.
  const goToPendingReviews = () =>
    navigate('/tenant-profile/document-hub?status=pending_review');

  if (isLoading) {
    return <CenteredMessage>{t('app.orgProfile.loading')}</CenteredMessage>;
  }
  if (isError) {
    return (
      <CenteredMessage tone="error">
        {t('app.orgProfile.loadError')}
      </CenteredMessage>
    );
  }
  if (!outline || outline.categories.length === 0) {
    return <CenteredMessage>{t('app.orgProfile.noTemplate')}</CenteredMessage>;
  }

  const activeDerived = activeMeta
    ? derivedById.get(activeMeta.id)
    : undefined;

  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: 'background.default',
        borderRadius: `${ORG_TOKENS.radiusLg}px`,
        // Keep the page scrollable but hide the scrollbar chrome.
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      <Box
        sx={{
          width: '100%',
          px: { xs: 1.5, sm: 2, md: 4 },
          py: { xs: 2, md: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 2, md: 2.5 },
          boxSizing: 'border-box',
        }}
      >
        <ProfileHeader />
        <ProfileHero />
        <ProfileSummaryRow
          summary={summary}
          completedCount={completedCount}
          categoryCount={categoryCount}
          docApprovedCount={docCounts.approved}
          docProcessingCount={docCounts.processing}
          onViewActivity={goToHub}
          onReviewAll={goToPendingReviews}
        />
        <GlobalUploadBar />
        <InFlightDocumentsBar />
        <CategoryTabs
          categories={outline.categories}
          derivedById={derivedById}
          activeId={activeId}
          onSelect={setActiveId}
        />
        {activeMeta && activeDerived && (
          <Box
            sx={{
              backgroundColor: 'background.paper',
              border: 1,
              borderColor: 'grey.300',
              borderRadius: `${ORG_TOKENS.radiusLg}px`,
              p: { xs: 1.5, sm: 2, md: 3 },
            }}
          >
            <CategoryPanel
              key={activeMeta.id}
              meta={activeMeta}
              derived={activeDerived}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
});

const CenteredMessage: FC<{ children: React.ReactNode; tone?: 'error' }> = observer(({
  children,
  tone,
}) => (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 4,
    }}
  >
    <Typography
      variant="textSM_regular"
      color={tone === 'error' ? 'yellow.800' : 'text.tertiary'}
    >
      {children}
    </Typography>
  </Box>
));
